import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { UserPlus, MailCheck } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/context/AuthContext'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { PasswordStrength, getStrengthScore } from '@/components/auth/PasswordStrength'
import { AuthAlert } from '@/components/auth/AuthAlert'
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton'

function formatApiError(err: any): string {
  const detail = err?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail.map((item: any) => item.msg || JSON.stringify(item)).join('. ')
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || JSON.stringify(detail)
  }
  return 'Registration failed. Please check your details and try again.'
}

export function Register() {
  const navigate = useNavigate()
  const { register } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  // Preserve invite links for existing beta users while making normal
  // public-beta registration code-free.
  const [inviteToken, setInviteToken] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [touched, setTouched] = useState({ email: false, password: false })

  useEffect(() => {
    const url = new URL(window.location.href)
    const token = url.searchParams.get('invite_token')
    const emailParam = url.searchParams.get('email')
    if (token) setInviteToken(token)
    if (emailParam) setEmail(emailParam)
  }, [])

  const emailError = useMemo(() => {
    if (!email) return 'Email is required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email address'
    return ''
  }, [email])

  const passwordError = useMemo(() => {
    if (!password) return 'Password is required'
    if (getStrengthScore(password) < 5) return 'Password does not meet all requirements'
    return ''
  }, [password])

  const canSubmit = !emailError && getStrengthScore(password) === 5

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({ email: true, password: true })
    setError('')

    if (!canSubmit) return

    setLoading(true)
    try {
      await register({
        email,
        password,
        display_name: displayName || undefined,
        invite_token: inviteToken || undefined,
      })
      navigate('/')
    } catch (err: any) {
      setError(formatApiError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-1.5">
        <h1 className="text-2xl font-bold tracking-tight">Create account</h1>
        <p className="text-sm text-muted-foreground">Modern email hosting. Multiple domains. Built for AI agents.</p>
        {inviteToken && (
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-3 py-1 text-xs text-primary">
            <MailCheck className="h-3 w-3" />
            Invite accepted
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <AuthAlert message={error} />}

        <div className="space-y-2">
          <Label htmlFor="name">Display Name</Label>
          <Input
            id="name"
            placeholder="Your name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            aria-invalid={touched.email && !!emailError}
            aria-describedby={touched.email && emailError ? 'email-error' : undefined}
            required
            autoComplete="email"
            autoFocus={!inviteToken}
          />
          {touched.email && emailError && (
            <p id="email-error" className="text-xs text-destructive">{emailError}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          {/* CHECKPOINT: PRD-01 FR-1.1.3 — Registration password minimum 8 characters. */}
          <PasswordInput
            id="password"
            placeholder="Min 8 characters, upper, lower, digit, special"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, password: true }))}
            aria-invalid={touched.password && !!passwordError}
            aria-describedby={touched.password && passwordError ? 'password-error' : 'password-help'}
            required
            autoComplete="new-password"
          />
          {touched.password && passwordError ? (
            <p id="password-error" className="text-xs text-destructive">{passwordError}</p>
          ) : (
            <p id="password-help" className="text-xs text-muted-foreground">
              8+ characters with uppercase, lowercase, digit, and special character.
            </p>
          )}
          <PasswordStrength password={password} />
        </div>

        <AuthSubmitButton loading={loading} disabled={!canSubmit}>
          <UserPlus className="mr-2 h-4 w-4" />
          Create account
        </AuthSubmitButton>
      </form>

      <div className="text-center text-sm">
        <span className="text-muted-foreground">Already have an account? </span>
        <Link to="/login" className="text-primary hover:underline font-medium">Sign in</Link>
      </div>
    </div>
  )
}
